import type { SearchFilterOption } from '@paction-2.0/ui';

import type { SearchBreed } from '@/features/breeds';
import type { NormalizedCatalogSearchFilters } from '@/features/catalog';
import { formatCityDisplay, getCityBySlug } from '@/features/locations';
import { prettifySegment } from '@/lib/format/segments';

type BreedOptionMeta = { isTargeting: boolean; photoUrl: string | null };

export const PROVINCE_OPTIONS = [
  { slug: 'alberta', label: 'Alberta' },
  { slug: 'british-columbia', label: 'British Columbia' },
  { slug: 'manitoba', label: 'Manitoba' },
  { slug: 'new-brunswick', label: 'New Brunswick' },
  { slug: 'newfoundland-and-labrador', label: 'Newfoundland & Labrador' },
  { slug: 'northwest-territories', label: 'Northwest Territories' },
  { slug: 'nova-scotia', label: 'Nova Scotia' },
  { slug: 'nunavut', label: 'Nunavut' },
  { slug: 'ontario', label: 'Ontario' },
  { slug: 'prince-edward-island', label: 'Prince Edward Island' },
  { slug: 'quebec', label: 'Québec' },
  { slug: 'saskatchewan', label: 'Saskatchewan' },
  { slug: 'yukon', label: 'Yukon' }
];

export const buildBreedOptions = (
  breeds: SearchBreed[]
): SearchFilterOption<BreedOptionMeta>[] => [
  {
    label: 'All breeds',
    value: undefined,
    meta: { isTargeting: true, photoUrl: null }
  },
  ...breeds
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((breed) => ({
      label: breed.name,
      value: breed.slug,
      meta: { isTargeting: breed.isTargeting, photoUrl: breed.photoUrl ?? null }
    }))
];

export const buildProvinceOptions = (): SearchFilterOption[] => [
  { label: 'All provinces', value: undefined },
  ...PROVINCE_OPTIONS.map((province) => ({
    label: province.label,
    value: province.slug
  }))
];

export const resolveBreedLabel = (
  breeds: SearchBreed[],
  breedSlug: NormalizedCatalogSearchFilters['breedSlug']
): string | undefined => {
  if (!breedSlug) return undefined;
  const found = breeds.find((breed) => breed.slug === breedSlug);
  // Use `||` (not `??`) so a breed record with an empty name still falls back to
  // the prettified slug — otherwise an empty name yields "" and downstream H1s
  // (e.g. BreedResultsHeader) render empty or not at all.
  return found?.name || prettifySegment(breedSlug);
};

export const resolveProvinceLabel = (
  provinceSlug: NormalizedCatalogSearchFilters['province']
): string | undefined => {
  if (!provinceSlug) return undefined;
  return (
    PROVINCE_OPTIONS.find((province) => province.slug === provinceSlug)
      ?.label ?? provinceSlug
  );
};

export const resolveCityLabel = (
  citySlug: NormalizedCatalogSearchFilters['city']
): string | undefined => {
  if (!citySlug) return undefined;
  const city = getCityBySlug(citySlug);
  return city ? formatCityDisplay(city) : citySlug;
};

export const resolveButtonLabel = (
  breedLabel: string | undefined,
  provinceLabel: string | undefined,
  cityLabel: string | undefined
): string => {
  if (breedLabel && cityLabel) return `${breedLabel} • ${cityLabel}`;
  if (breedLabel && provinceLabel) return `${breedLabel} • ${provinceLabel}`;
  if (breedLabel) return breedLabel;
  if (cityLabel) return cityLabel;
  if (provinceLabel) return provinceLabel;
  return 'Find a puppy';
};
